/**
 * Configuración guiada — BarmetHub Guided Setup (v2)
 * SUPERADMIN ONLY
 *
 * Real configuration process — no "mark as complete" button.
 * Steps auto-detect existing config. CTAs lead to real actions.
 * States: DETECTED | CONFIGURED | CONNECTED | VERIFIED | PENDING | OPTIONAL | SKIPPED | REQUIRES_ACTION | ERROR
 *
 * Per BHM-HMS-REQ-034.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../utils/api';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';

// ── Types ───────────────────────────────────────────────────────────────
export type StepStatus =
  | 'DETECTED'       // Auto-detected as already configured
  | 'CONFIGURED'     // Manually configured
  | 'CONNECTED'      // Connection verified
  | 'VERIFIED'       // Full verification passed
  | 'PENDING'        // Needs action
  | 'OPTIONAL'       // Optional, not yet configured
  | 'SKIPPED'        // User chose to skip (optional only)
  | 'REQUIRES_ACTION' // Blocked by missing dependency
  | 'ERROR';         // Configuration error

export type ConnectionMethod = 'OAUTH' | 'API_KEY' | 'TOKEN' | 'MCP' | 'CREDENTIAL' | 'ALREADY_CONFIGURED' | 'MANUAL_CONFIG' | 'NAVIGATE';

export interface SetupAction {
  id: string;
  label: string;
  icon: string;
  method: ConnectionMethod;
  /** Where to navigate or what to open */
  target: string;
  status: StepStatus;
  required: boolean;
  description: string;
}

export interface SetupStep {
  id: string;
  number: number;
  title: string;
  icon: string;
  status: StepStatus;
  description: string;
  required: boolean;
  actions: SetupAction[];
}

interface SetupState {
  currentStepId: string;
  stepStates: Record<string, StepStatus>;
  actionStates: Record<string, StepStatus>;
  startedAt: string;
  completedAt?: string;
}

interface ConfiguracionGuiadaProps {
  open: boolean;
  onClose: () => void;
  fullWorkspace?: boolean;
}

const STORAGE_KEY = 'barmethub-configuracion-guiada';

// ── Step definitions with real actions ──────────────────────────────────
function getSteps(): SetupStep[] {
  return [
    {
      id: 'org', number: 1, title: 'Organización', icon: 'business',
      status: 'PENDING', description: 'Datos de la organización',
      required: true,
      actions: [
        { id: 'org-name', label: 'Revisar datos de organización', icon: 'edit', method: 'NAVIGATE', target: '/ajustes/mi-cuenta', status: 'PENDING', required: true, description: 'Nombre, zona horaria, idioma' },
      ],
    },
    {
      id: 'security-init', number: 2, title: 'Seguridad inicial', icon: 'shield',
      status: 'PENDING', description: 'Autenticación y protección',
      required: true,
      actions: [
        { id: 'sec-password', label: 'Contraseña configurada', icon: 'lock', method: 'ALREADY_CONFIGURED', target: '', status: 'DETECTED', required: true, description: 'Contraseña maestra activa' },
        { id: 'sec-mfa', label: 'Activar MFA', icon: 'security', method: 'NAVIGATE', target: '/ajustes/mi-cuenta', status: 'PENDING', required: false, description: 'Autenticación en dos pasos' },
        { id: 'sec-passkey', label: 'Configurar Passkey', icon: 'key', method: 'NAVIGATE', target: '/ajustes/mi-cuenta', status: 'PENDING', required: false, description: 'Acceso sin contraseña' },
        { id: 'sec-lock', label: 'Configurar bloqueo de pantalla', icon: 'lock_clock', method: 'NAVIGATE', target: '/ajustes/mi-cuenta', status: 'PENDING', required: false, description: 'Bloqueo automático por inactividad' },
        { id: 'sec-recovery', label: 'Configurar recuperación', icon: 'replay', method: 'NAVIGATE', target: '/ajustes/mi-cuenta', status: 'PENDING', required: false, description: 'Kit de recuperación de cuenta' },
      ],
    },
    {
      id: 'integrations', number: 3, title: 'Integraciones', icon: 'hub',
      status: 'OPTIONAL', description: 'Conectar servicios externos',
      required: false,
      actions: [
        { id: 'int-holded', label: 'Conectar Holded', icon: 'account_balance', method: 'API_KEY', target: '/ajustes/integraciones', status: 'PENDING', required: false, description: 'Facturación y contabilidad' },
        { id: 'int-clientify', label: 'Estado de Clientify (integración global)', icon: 'contacts', method: 'NAVIGATE', target: '/ajustes/integraciones', status: 'PENDING', required: false, description: 'CRM y ventas · integración global gestionada por el superadministrador' },
        { id: 'int-metricool', label: 'Conectar Metricool', icon: 'analytics', method: 'API_KEY', target: '/ajustes/integraciones', status: 'PENDING', required: false, description: 'Social media analytics' },
        { id: 'int-gsc', label: 'Conectar Google Search Console', icon: 'search', method: 'OAUTH', target: '/ajustes/integraciones', status: 'PENDING', required: false, description: 'SEO y rendimiento web' },
        { id: 'int-gmail', label: 'Conectar Gmail', icon: 'mail', method: 'OAUTH', target: '/ajustes/integraciones', status: 'PENDING', required: false, description: 'Correo corporativo' },
      ],
    },
    {
      id: 'users', number: 4, title: 'Usuarios', icon: 'group',
      status: 'PENDING', description: 'Invitar y configurar usuarios',
      required: true,
      actions: [
        { id: 'usr-invite', label: 'Añadir usuario', icon: 'person_add', method: 'NAVIGATE', target: '/ajustes/usuarios', status: 'PENDING', required: false, description: 'Invitar miembros del equipo' },
      ],
    },
    {
      id: 'roles', number: 5, title: 'Roles y permisos', icon: 'policy',
      status: 'PENDING', description: 'Definir matriz de acceso',
      required: true,
      actions: [
        { id: 'roles-config', label: 'Configurar permisos', icon: 'admin_panel_settings', method: 'NAVIGATE', target: '/ajustes/usuarios', status: 'PENDING', required: true, description: 'Matriz de permisos por rol' },
      ],
    },
    {
      id: 'operations', number: 6, title: 'Operaciones', icon: 'account_balance',
      status: 'OPTIONAL', description: 'Administración, CRM, Marketing, Webs',
      required: false,
      actions: [
        { id: 'ops-admin', label: 'Configurar Administración', icon: 'account_balance', method: 'NAVIGATE', target: '/operaciones/administracion', status: 'PENDING', required: false, description: 'Holded, facturación' },
        { id: 'ops-crm', label: 'Configurar CRM', icon: 'contacts', method: 'NAVIGATE', target: '/operaciones/crm', status: 'PENDING', required: false, description: 'Clientify, ventas' },
        { id: 'ops-marketing', label: 'Configurar Marketing', icon: 'campaign', method: 'NAVIGATE', target: '/operaciones/marketing', status: 'PENDING', required: false, description: 'Metricool, campañas' },
      ],
    },
    {
      id: 'projects', number: 7, title: 'Proyectos', icon: 'folder_open',
      status: 'PENDING', description: 'Estructura de proyectos y clientes',
      required: true,
      actions: [
        { id: 'proj-create', label: 'Crear primer proyecto', icon: 'add_circle', method: 'NAVIGATE', target: '/proyectos', status: 'PENDING', required: false, description: 'Alta de proyecto/cliente' },
      ],
    },
    {
      id: 'dashboard', number: 8, title: 'Dashboard', icon: 'dashboard',
      status: 'DETECTED', description: 'Dashboard configurado automáticamente',
      required: true,
      actions: [],
    },
    {
      id: 'hermes', number: 9, title: 'Nexus', icon: 'bolt',
      status: 'OPTIONAL', description: 'Configurar Nexus, la IA operativa de BarmetHub',
      required: false,
      actions: [
        { id: 'hermes-activate', label: 'Abrir Nexus', icon: 'bolt', method: 'MCP', target: '/nexus', status: 'PENDING', required: false, description: 'IA operativa de BarmetHub' },
      ],
    },
    {
      id: 'agents', number: 10, title: 'Agentes', icon: 'smart_toy',
      status: 'OPTIONAL', description: 'Configurar agentes IA',
      required: false,
      actions: [
        { id: 'agents-config', label: 'Configurar agentes', icon: 'smart_toy', method: 'NAVIGATE', target: '/nexus/agentes', status: 'PENDING', required: false, description: 'Agentes autónomos' },
      ],
    },
    {
      id: 'skills', number: 11, title: 'Skills / MCP / Tools', icon: 'school',
      status: 'OPTIONAL', description: 'Herramientas y capacidades',
      required: false,
      actions: [
        { id: 'skills-mcp', label: 'Configurar MCP', icon: 'terminal', method: 'MCP', target: '/nexus/mcp', status: 'PENDING', required: false, description: 'Model Context Protocol' },
      ],
    },
    {
      id: 'factory-prompts', number: 12, title: 'Recursos', icon: 'auto_awesome',
      status: 'OPTIONAL', description: 'Lasspass y Factory Prompts',
      required: false,
      actions: [
        { id: 'lasspass-ready', label: 'Módulo Lasspass', icon: 'key', method: 'NAVIGATE', target: '/recursos/lasspass', status: 'PENDING', required: false, description: 'Estado detectado automáticamente (READY / NOT READY)' },
        { id: 'prompts-ready', label: 'Módulo Factory Prompts', icon: 'auto_awesome', method: 'NAVIGATE', target: '/recursos/factory-prompts', status: 'PENDING', required: false, description: 'Estado detectado automáticamente (READY / NOT READY)' },
        { id: 'factory-multi-ia', label: 'Factory IA multi-IA', icon: 'hub', method: 'NAVIGATE', target: '/recursos/factory-prompts/factory', status: 'PENDING', required: false, description: 'MULTI_IA_READY solo con 2 o más IAs conectadas; con una sola, modo degradado (la biblioteca sigue READY)' },
      ],
    },
    {
      id: 'automations', number: 13, title: 'Automatizaciones', icon: 'automation',
      status: 'OPTIONAL', description: 'Workflows y automatización',
      required: false,
      actions: [],
    },
    {
      id: 'validation', number: 14, title: 'Validación', icon: 'verified',
      status: 'PENDING', description: 'Verificar configuración completa',
      required: true,
      actions: [],
    },
    {
      id: 'finish', number: 15, title: 'Finalizar', icon: 'check_circle',
      status: 'PENDING', description: 'Activar y confirmar',
      required: true,
      actions: [],
    },
  ];
}

// ── Persistence ─────────────────────────────────────────────────────────
function loadState(): SetupState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

function saveState(state: SetupState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

function initState(): SetupState {
  const existing = loadState();
  if (existing) return existing;
  const stepStates: Record<string, StepStatus> = {};
  const actionStates: Record<string, StepStatus> = {};
  const steps = getSteps();
  steps.forEach(s => {
    stepStates[s.id] = s.status;
    s.actions.forEach(a => { actionStates[a.id] = a.status; });
  });
  return {
    currentStepId: steps[0].id,
    stepStates,
    actionStates,
    startedAt: new Date().toISOString(),
  };
}

// ── Progress helpers ────────────────────────────────────────────────────
const DONE_STATUSES: StepStatus[] = ['DETECTED', 'CONFIGURED', 'CONNECTED', 'VERIFIED'];

function isStepDone(status: StepStatus): boolean {
  return DONE_STATUSES.includes(status) || status === 'SKIPPED';
}

function getStepStatus(step: SetupStep, state: SetupState): StepStatus {
  // If all actions are done, step is done
  if (step.actions.length > 0) {
    const actionStatuses = step.actions.map(a => state.actionStates[a.id] || a.status);
    const requiredActions = step.actions.filter(a => a.required);
    const requiredDone = requiredActions.every(a => isStepDone(state.actionStates[a.id] || a.status));
    const anyDone = actionStatuses.some(a => isStepDone(a));
    if (requiredDone && anyDone) return 'CONFIGURED';
    if (state.stepStates[step.id] === 'SKIPPED') return 'SKIPPED';
    return state.stepStates[step.id] || step.status;
  }
  return state.stepStates[step.id] || step.status;
}

function getEssentialProgress(state: SetupState): { done: number; total: number; pct: number } {
  const steps = getSteps();
  const required = steps.filter(s => s.required);
  const done = required.filter(s => isStepDone(getStepStatus(s, state))).length;
  return { done, total: required.length, pct: Math.round((done / required.length) * 100) };
}

function getTotalProgress(state: SetupState): { done: number; total: number; pct: number } {
  const steps = getSteps();
  const done = steps.filter(s => isStepDone(getStepStatus(s, state))).length;
  return { done, total: steps.length, pct: Math.round((done / steps.length) * 100) };
}

function canNavigateTo(targetId: string, state: SetupState): boolean {
  const steps = getSteps();
  const targetIdx = steps.findIndex(s => s.id === targetId);
  const currentIdx = steps.findIndex(s => s.id === state.currentStepId);
  if (targetIdx <= currentIdx) return true;
  // Can go forward only if current required actions are done
  const currentStep = steps[currentIdx];
  const currentStatus = getStepStatus(currentStep, state);
  return isStepDone(currentStatus);
}

// ── Status Badge ────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: StepStatus }) {
  const config: Record<StepStatus, { label: string; className: string }> = {
    DETECTED: { label: 'Detectado', className: 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' },
    CONFIGURED: { label: 'Configurado', className: 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' },
    CONNECTED: { label: 'Conectado', className: 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' },
    VERIFIED: { label: 'Verificado', className: 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' },
    PENDING: { label: 'Pendiente', className: 'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30' },
    OPTIONAL: { label: 'Opcional', className: 'bg-surface-container-high text-on-surface-variant border-outline-variant' },
    SKIPPED: { label: 'Omitido', className: 'bg-surface-container-high text-on-surface-variant/60 border-outline-variant' },
    REQUIRES_ACTION: { label: 'Requiere acción', className: 'bg-[#F43F5E]/20 text-[#F43F5E] border-[#F43F5E]/30' },
    ERROR: { label: 'Error', className: 'bg-[#F43F5E]/20 text-[#F43F5E] border-[#F43F5E]/30' },
  };
  const c = config[status] || config.PENDING;
  return (
    <span className={clsx('inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider', c.className)}>
      {c.label}
    </span>
  );
}

// ── Action Button ───────────────────────────────────────────────────────
function ActionRow({ action, onNavigate, onSkip }: {
  action: SetupAction;
  onNavigate: (target: string) => void;
  onSkip: () => void;
}) {
  const done = isDone(action.status);
  return (
    <div className="flex items-center justify-between rounded-lg border border-outline-variant bg-surface-container-lowest px-4 py-3">
      <div className="flex items-center gap-3 min-w-0">
        <span className={clsx(
          'material-symbols-outlined text-lg',
          done ? 'text-[#10B981]' : 'text-on-surface-variant'
        )}>{action.icon}</span>
        <div className="min-w-0">
          <p className={clsx('text-sm font-medium truncate', done ? 'text-[#10B981]' : 'text-on-surface')}>
            {action.label}
          </p>
          <p className="text-xs text-on-surface-variant truncate">{action.description}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {done ? (
          <span className="text-[#10B981] text-xs font-medium">✓ {statusLabel(action.status)}</span>
        ) : action.status === 'SKIPPED' ? (
          <button onClick={onSkip} className="text-xs text-on-surface-variant hover:text-[#FF5E1E] transition-colors">
            Configurar
          </button>
        ) : (
          <>
            {!action.required && (
              <button onClick={onSkip} className="text-xs text-on-surface-variant/60 hover:text-on-surface-variant transition-colors">
                Omitir
              </button>
            )}
            <button
              onClick={() => onNavigate(action.target)}
              className="rounded-lg bg-gradient-to-r from-[#FF5E1E] to-[#D946EF] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 transition-opacity"
            >
              {getActionLabel(action)}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function isDone(status: StepStatus): boolean {
  return DONE_STATUSES.includes(status);
}

function statusLabel(status: StepStatus): string {
  const labels: Partial<Record<StepStatus, string>> = { DETECTED: 'Detectado', CONFIGURED: 'Configurado', CONNECTED: 'Conectado', VERIFIED: 'Verificado' };
  return labels[status] || '';
}

function getActionLabel(action: SetupAction): string {
  switch (action.method) {
    case 'OAUTH': return 'Conectar';
    case 'API_KEY': return 'Configurar API';
    case 'TOKEN': return 'Configurar token';
    case 'MCP': return 'Configurar MCP';
    case 'CREDENTIAL': return 'Configurar credenciales';
    case 'NAVIGATE': return action.label.startsWith('Añadir') || action.label.startsWith('Crear') ? action.label : 'Configurar';
    default: return 'Configurar';
  }
}

// ── Step Content ────────────────────────────────────────────────────────
function StepContent({ step, state, onNavigate, onSkipStep, onSkipAction }: {
  step: SetupStep;
  state: SetupState;
  onNavigate: (target: string) => void;
  onSkipStep: () => void;
  onSkipAction: (actionId: string) => void;
}) {
  const stepStatus = getStepStatus(step, state);
  const done = isDone(stepStatus);
  const actions = step.actions.map(a => ({ ...a, status: state.actionStates[a.id] || a.status }));
  const allRequiredDone = actions.filter(a => a.required).every(a => isDone(a.status));

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={clsx(
            'flex h-10 w-10 items-center justify-center rounded-lg text-lg font-bold text-white',
            done ? 'bg-[#10B981]' : 'bg-gradient-to-br from-[#FF5E1E] to-[#D946EF]'
          )}>
            {done ? '✓' : step.number}
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-on-surface">{step.title}</h3>
              {!step.required && (
                <span className="text-[10px] text-on-surface-variant/60 uppercase tracking-wider">(opcional)</span>
              )}
            </div>
            <p className="text-sm text-on-surface-variant">{step.description}</p>
          </div>
        </div>
        <StatusBadge status={stepStatus} />
      </div>

      {/* Actions */}
      {actions.length > 0 ? (
        <div className="space-y-2">
          {actions.map(action => (
            <ActionRow
              key={action.id}
              action={action}
              onNavigate={onNavigate}
              onSkip={() => onSkipAction(action.id)}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-outline-variant bg-surface-container-lowest p-6">
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <span className="material-symbols-outlined text-4xl text-on-surface-variant/40">{step.icon}</span>
            <p className="text-sm text-on-surface-variant">
              {done ? 'Este paso está completo.' : 'Este paso se completa automáticamente.'}
            </p>
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div className="flex items-center gap-3 justify-end">
        {!step.required && stepStatus !== 'SKIPPED' && !done && (
          <button
            onClick={onSkipStep}
            className="rounded-lg border border-outline-variant px-4 py-2 text-sm text-on-surface-variant hover:bg-surface-container-high transition-colors"
          >
            Omitir por ahora
          </button>
        )}
        {stepStatus === 'SKIPPED' && (
          <button
            onClick={onSkipStep}
            className="rounded-lg border border-[#FF5E1E]/30 px-4 py-2 text-sm text-[#FF5E1E] hover:bg-[#FF5E1E]/10 transition-colors"
          >
            Reactivar
          </button>
        )}
        {allRequiredDone && actions.length > 0 && !done && (
          <button
            onClick={() => onSkipStep()} // Will be replaced with auto-advance
            className="rounded-lg bg-gradient-to-r from-[#FF5E1E] to-[#D946EF] px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
          >
            Continuar
          </button>
        )}
      </div>
    </div>
  );
}

// ── Progress Bar (essential + total) ────────────────────────────────────
function ProgressBar({ state }: { state: SetupState }) {
  const essential = getEssentialProgress(state);
  const total = getTotalProgress(state);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <span className="text-[10px] text-on-surface-variant w-16">Esencial</span>
        <div className="h-2 flex-1 rounded-full bg-surface-container-high overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-[#FF5E1E] to-[#D946EF] transition-all duration-500" style={{ width: `${essential.pct}%` }} />
        </div>
        <span className="font-mono text-xs text-on-surface-variant w-20 text-right">{essential.pct}% · {essential.done}/{essential.total}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-[10px] text-on-surface-variant w-16">Total</span>
        <div className="h-1.5 flex-1 rounded-full bg-surface-container-high overflow-hidden">
          <div className="h-full rounded-full bg-[#06B6D4]/50 transition-all duration-500" style={{ width: `${total.pct}%` }} />
        </div>
        <span className="font-mono text-xs text-on-surface-variant w-20 text-right">{total.pct}% · {total.done}/{total.total}</span>
      </div>
    </div>
  );
}

// ── Header Label ────────────────────────────────────────────────────────
export function getConfiguracionLabel(state: SetupState | null): string {
  if (!state) return 'Configuración guiada';
  const essential = getEssentialProgress(state);
  if (essential.pct === 100) {
    const total = getTotalProgress(state);
    if (total.pct === 100) return 'Configuración completa';
    return `Configuración lista · ${total.pct}% total`;
  }
  return `Configuración guiada · ${essential.pct}%`;
}

// ── Main Component ──────────────────────────────────────────────────────
export default function ConfiguracionGuiada({ open, onClose, fullWorkspace = false }: ConfiguracionGuiadaProps) {
  const [state, setState] = useState<SetupState>(initState);
  const [mode, setMode] = useState<'drawer' | 'workspace'>(fullWorkspace ? 'workspace' : 'drawer');
  const navigate = useNavigate();

  const steps = useMemo(() => getSteps(), []);
  const activeStep = useMemo(() => steps.find(s => s.id === state.currentStepId) || steps[0], [steps, state.currentStepId]);
  const essential = getEssentialProgress(state);
  const total = getTotalProgress(state);

  // Persist on every change
  useEffect(() => { saveState(state); }, [state]);

  // Auto-detect Factory Prompts module status (READY / NOT READY) — never manual.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get<{ moduleStatus?: string }>('/prompts/meta')
      .then(({ data }) => data.moduleStatus === 'READY' ? 'DETECTED' : 'REQUIRES_ACTION')
      .catch(() => 'ERROR' as const)
      .then((status) => {
        if (cancelled) return;
        setState(prev => ({ ...prev, actionStates: { ...prev.actionStates, 'prompts-ready': status as StepStatus } }));
      });
    api.get<{ multiIa?: string }>('/prompts/factory/providers')
      .then(({ data }) => (data.multiIa === 'READY' ? 'DETECTED' : 'REQUIRES_ACTION'))
      .catch(() => 'REQUIRES_ACTION' as const)
      .then((status) => {
        if (cancelled) return;
        setState(prev => ({ ...prev, actionStates: { ...prev.actionStates, 'factory-multi-ia': status as StepStatus } }));
      });
    api.get('/vaults')
      .then(() => 'DETECTED' as const)
      .catch(() => 'REQUIRES_ACTION' as const)
      .then((status) => {
        if (cancelled) return;
        setState(prev => ({ ...prev, actionStates: { ...prev.actionStates, 'lasspass-ready': status as StepStatus } }));
      });
    return () => { cancelled = true; };
  }, [open]);

  // Keyboard shortcut
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Prevent body scroll
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const navigateToStep = useCallback((stepId: string) => {
    if (canNavigateTo(stepId, state)) {
      setState(prev => ({ ...prev, currentStepId: stepId }));
    }
  }, [state]);

  const handleNavigate = useCallback((target: string) => {
    // Save current state, close drawer, navigate to target
    onClose();
    navigate(target);
  }, [onClose, navigate]);

  const handleSkipStep = useCallback(() => {
    setState(prev => {
      const currentStep = steps.find(s => s.id === prev.currentStepId);
      if (!currentStep) return prev;
      const currentStatus = getStepStatus(currentStep, prev);
      const newStatus: StepStatus = currentStatus === 'SKIPPED' ? (currentStep.required ? 'PENDING' : 'OPTIONAL') : 'SKIPPED';
      const newStepStates = { ...prev.stepStates, [prev.currentStepId]: newStatus };
      // Auto-advance if skipping
      const nextIdx = steps.findIndex(s => s.id === prev.currentStepId) + 1;
      const nextStep = nextIdx < steps.length ? steps[nextIdx] : null;
      return {
        ...prev,
        stepStates: newStepStates,
        currentStepId: newStatus === 'SKIPPED' && nextStep ? nextStep.id : prev.currentStepId,
      };
    });
  }, [steps]);

  const handleSkipAction = useCallback((actionId: string) => {
    setState(prev => {
      const currentStatus = prev.actionStates[actionId];
      const newStatus: StepStatus = currentStatus === 'SKIPPED' ? 'PENDING' : 'SKIPPED';
      return { ...prev, actionStates: { ...prev.actionStates, [actionId]: newStatus } };
    });
  }, []);

  const handleSaveAndClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!open) return null;

  const essentialDone = essential.pct === 100;

  const content = (
    <div className="fixed inset-0 z-[100] flex justify-end">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleSaveAndClose} />

      <div className={clsx(
        'relative flex h-full flex-col bg-surface-container-low border-l border-outline-variant shadow-2xl transition-all duration-300',
        mode === 'drawer' ? 'w-[42%] min-w-[400px]' : 'w-full'
      )}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-outline-variant px-6 py-4">
          <div className="flex items-center gap-3">
            <div className={clsx(
              'flex h-8 w-8 items-center justify-center rounded-lg',
              essentialDone ? 'bg-[#10B981]' : 'bg-gradient-to-br from-[#FF5E1E] to-[#D946EF]'
            )}>
              <span className="material-symbols-outlined text-sm text-white">
                {essentialDone ? 'check_circle' : 'tune'}
              </span>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-on-surface">
                {essentialDone ? 'Configuración lista' : 'Configuración guiada'}
              </h2>
              <p className="text-[10px] text-on-surface-variant">
                {essentialDone
                  ? `Esencial completa · ${total.pct}% total`
                  : `Esencial: ${essential.pct}% · Total: ${total.pct}%`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setMode(m => m === 'drawer' ? 'workspace' : 'drawer')} className="rounded-lg p-2 text-on-surface-variant hover:bg-surface-container-high transition-colors">
              <span className="material-symbols-outlined text-sm">{mode === 'drawer' ? 'open_in_full' : 'close_fullscreen'}</span>
            </button>
            <button onClick={handleSaveAndClose} className="rounded-lg p-2 text-on-surface-variant hover:bg-surface-container-high transition-colors">
              <span className="material-symbols-outlined text-sm">close</span>
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="border-b border-outline-variant px-6 py-3">
          <ProgressBar state={state} />
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Step Navigation */}
          <nav className="w-64 shrink-0 overflow-y-auto border-r border-outline-variant p-4 space-y-1">
            {steps.map(step => {
              const stepStatus = getStepStatus(step, state);
              const isActive = step.id === state.currentStepId;
              const canNav = canNavigateTo(step.id, state);
              const done = isDone(stepStatus);

              return (
                <button key={step.id} onClick={() => navigateToStep(step.id)} disabled={!canNav}
                  className={clsx(
                    'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all text-sm',
                    !canNav && 'opacity-40 cursor-not-allowed',
                    isActive ? 'bg-gradient-to-r from-[#FF5E1E]/10 to-[#D946EF]/10 text-on-surface border border-[#FF5E1E]/20'
                      : canNav ? 'text-on-surface-variant hover:bg-surface-container-high' : ''
                  )}>
                  <span className={clsx(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs font-bold',
                    done ? 'bg-[#10B981]/20 text-[#10B981]'
                      : stepStatus === 'SKIPPED' ? 'bg-surface-container-high text-on-surface-variant/60'
                        : isActive ? 'bg-gradient-to-br from-[#FF5E1E] to-[#D946EF] text-white'
                          : 'bg-surface-container-high text-on-surface-variant'
                  )}>
                    {done ? '✓' : stepStatus === 'SKIPPED' ? '–' : step.number}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{step.title}</p>
                    {!step.required && <p className="truncate text-[9px] text-on-surface-variant/40">opcional</p>}
                  </div>
                </button>
              );
            })}
          </nav>

          {/* Step Content */}
          <div className="flex-1 overflow-y-auto">
            <StepContent
              step={activeStep}
              state={state}
              onNavigate={handleNavigate}
              onSkipStep={handleSkipStep}
              onSkipAction={handleSkipAction}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-outline-variant px-6 py-3">
          <p className="text-xs text-on-surface-variant/60">
            Iniciado: {new Date(state.startedAt).toLocaleString('es-ES')}
          </p>
          <button onClick={handleSaveAndClose} className="rounded-lg border border-outline-variant px-4 py-2 text-sm text-on-surface-variant hover:bg-surface-container-high transition-colors">
            Guardar y continuar después
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}